import { formatDateScalarString } from '@/common/dates';
import { LogoutButton } from '@/components/buttons';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { useCurrentUser } from '@/hooks';
import { ContentWithLoad } from '@/layouts/content-with-load';
import { PrimaryLayout } from '@/layouts/primary';
import { TopNavFor } from '@/layouts/primary/top-nav';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { AccountForm, AccountFormValues } from './form';
import { useGetUserAccountQuery, useUpdateUserAccountMutation } from './graphql.generated';

export const AccountPage = () => {
	const navigate = useNavigate();
	const { currentUser, loading: userLoading, refetchUser, logUserOut } = useCurrentUser();
	const { to } = useSearch({ from: '/_app/account' });
	const { data, loading, error } = useGetUserAccountQuery({
		variables: { id: currentUser?.id || '' },
		skip: !currentUser?.id,
	});
	const [updateUserAccount, { loading: updatingUser }] = useUpdateUserAccountMutation();

	const formRef = useRef<FormikProps<AccountFormValues>>(null);
	const submitForm = useCallback(() => {
		formRef.current?.submitForm();
	}, []);

	const saveProfileButton = useMemo(
		() => (
			<StandardButton loading={updatingUser} onClick={submitForm}>
				Save Profile
			</StandardButton>
		),
		[submitForm, updatingUser]
	);

	const logoutButton = useMemo(() => <LogoutButton />, []);

	const handleSubmit = useCallback(
		async (values: AccountFormValues) => {
			try {
				if (!currentUser?.id) {
					throw new Error('User is not authenticated');
				}
				await updateUserAccount({
					variables: {
						input: {
							formattedAddress: values.formattedAddress,
							addressLine1: values.addressLine1,
							addressLine2: values.addressLine2,
							city: values.city,
							dateOfBirth: formatDateScalarString(values.dateOfBirth),
							council: values.council,
							dwellingType: values.dwellingType,
							email: values.email.toLowerCase(),
							firstName: values.firstName,
							gender: values.gender,
							householdSize: values.householdSize || null,
							isOver18: values.isOver18,
							isParticipatingInPrizeDraw: values.isParticipatingInPrizeDraw,
							lastName: values.lastName,
							phoneNumber: values.phoneNumber,
							postcode: values.postcode,
							state: values.state,
							lat: values.lat,
							lng: values.lng,
							isManualAddress: values.isManualAddress,
						},
					},
				});

				if (currentUser?.contact?.email?.toLowerCase() !== values.email.toLowerCase()) {
					await logUserOut();
					navigate({ to: '/welcome' });
				} else {
					await refetchUser();
					navigate({ to: to ? `/${to}` : '/home' });
				}
			} catch (error) {
				// TODO: need some error handling to give the user feedback
				console.error(error);
			}
		},
		[currentUser, updateUserAccount, navigate, to, refetchUser, logUserOut]
	);

	const handleChangePassword = () => navigate({ to: '/change-password' });

	const handleDeleteAccount = () => navigate({ to: '/delete-account' });

	const initialValues: AccountFormValues = useMemo(() => {
		return {
			firstName: data?.user?.contact?.firstName || '',
			lastName: data?.user?.contact?.lastName || '',
			email: data?.user?.contact?.email?.toLowerCase() || '',
			phoneNumber: data?.user?.phoneNumber || '',
			formattedAddress: data?.user?.formattedAddress || '',
			addressLine1: data?.user?.addressLine1 || '',
			addressLine2: data?.user?.addressLine2 || '',
			city: data?.user?.city || '',
			council: data?.user?.council || '',
			postcode: data?.user?.postcode || '',
			state: data?.user?.state || '',
			gender: data?.user?.gender || '',
			dateOfBirth: data?.user?.dateOfBirth ? new Date(data.user.dateOfBirth) : null,
			dwellingType: data?.user?.dwellingType || '',
			householdSize: data?.user?.householdSize || null,
			isOver18: data?.user?.isOver18 || false,
			isParticipatingInPrizeDraw: data?.user?.isParticipatingInPrizeDraw || false,
			isManualAddress: data?.user?.isManualAddress || false,
			lat: data?.user?.lat || null,
			lng: data?.user?.lng || null,
		};
	}, [data?.user]);

	return (
		<PrimaryLayout
			header={{ title: 'Account', backTo: '/home', children: logoutButton }}
			topNav={TopNavFor.ACCOUNT}
			footer={{ children: saveProfileButton }}
		>
			<ContentWithLoad
				loading={loading || userLoading}
				error={!!error}
				isEmpty={!data?.user}
				isEmptyMessage="Please log in or refresh to view your account"
				isErrorMessage="An error occurred while loading your account"
			>
				<ContentPanel gap="md">
					<AccountForm formRef={formRef} onSubmit={handleSubmit} initialValues={initialValues} />
					<StandardButton variant="transparent" onClick={handleChangePassword}>
						Change Password
					</StandardButton>
					<StandardButton variant="danger" onClick={handleDeleteAccount}>
						Delete Account
					</StandardButton>
				</ContentPanel>
			</ContentWithLoad>
		</PrimaryLayout>
	);
};
